import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import {
  ShieldCheck,
  Star,
  Handshake,
  Lightbulb,
  Check,
  Ear,
  MessageSquare,
  CalendarClock,
  Users,
  Building2
} from "lucide-react";

const AboutPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    navigate("/#contact");
  };

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Header scrolled={scrolled} />
      
      <main>
        {/* Page Header */}
        <section className="py-16 md:py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="page-header">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="mb-5 text-sm" style={{ color: "#6B7280" }} data-testid="breadcrumb">
              <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
              <span className="mx-2">›</span>
              <span>About</span>
            </nav>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: "#1F2937" }} data-testid="page-title">
              About A3 Technologies
            </h1>
            <p className="text-lg" style={{ color: "#4A5568" }}>
              Your trusted technology partner
            </p>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 px-5 bg-white" data-testid="our-story">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
              <div className="flex justify-center">
                <div className="w-72 h-72 md:w-80 md:h-80 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#F9FAFB" }}>
                  <Building2 size={150} style={{ color: "#3B82F6" }} />
                </div>
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: "#1F2937" }}>
                  Who We Are
                </h2>
                <p className="text-lg mb-5 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.8" }}>
                  A3 Technologies was founded with a simple mission: to provide small and medium businesses with enterprise-level IT support at an affordable price.
                </p>
                <p className="text-lg mb-5 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.8" }}>
                  We understand that technology can be overwhelming, especially for businesses that don't have dedicated IT staff. That's why we take the time to understand your business, explain things in plain language, and provide solutions that actually work for you.
                </p>
                <p className="text-lg mb-5 leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.8" }}>
                  Our team of certified IT professionals brings years of experience helping businesses like yours leverage technology to grow, compete, and succeed in today's digital world.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.8" }}>
                  Whether you need ongoing IT support, help with a specific project, or strategic guidance, we're here to be your trusted technology partner.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="values-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              What Drives Us
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <ValueCard 
                Icon={ShieldCheck} 
                title="Integrity" 
                description="We do what's right, even when no one is watching. Our recommendations are always based on your best interests, not ours."
                color="#3B82F6"
              />
              <ValueCard 
                Icon={Star} 
                title="Excellence" 
                description="We're committed to delivering the highest quality service. We stay current with technology and continuously improve our skills."
                color="#2C5F2D"
              />
              <ValueCard 
                Icon={Handshake} 
                title="Partnership" 
                description="Your success is our success. We build long-term relationships based on trust, transparency, and mutual respect."
                color="#3B82F6"
              />
              <ValueCard 
                Icon={Lightbulb} 
                title="Innovation" 
                description="We stay ahead of technology trends to keep you competitive. We're always looking for better ways to solve problems."
                color="#2C5F2D"
              />
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 px-5 bg-white" data-testid="why-choose-us">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Why Work With A3 Technologies?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4">
              <div>
                <BenefitItem text="Certified IT Professionals" />
                <BenefitItem text="Years of Industry Experience" />
                <BenefitItem text="Responsive, Local Support" />
                <BenefitItem text="Transparent, Honest Pricing" />
              </div>
              <div>
                <BenefitItem text="Proven Track Record" />
                <BenefitItem text="Client-Focused Approach" />
                <BenefitItem text="Proactive Problem Solving" />
                <BenefitItem text="No Long-Term Contracts Required" />
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="approach-section">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              How We Work
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <ApproachCard 
                Icon={Ear} 
                title="Listen First" 
                description="We take the time to understand your business, challenges, and goals before recommending solutions."
                color="#3B82F6"
              />
              <ApproachCard 
                Icon={MessageSquare} 
                title="Plain Language" 
                description="We explain technology in terms you can understand, without confusing jargon or technical speak."
                color="#2C5F2D"
              />
              <ApproachCard 
                Icon={CalendarClock} 
                title="Long-Term Thinking" 
                description="We focus on sustainable solutions that will serve your business well into the future, not just quick fixes."
                color="#3B82F6"
              />
            </div>
          </div>
        </section>

        {/* Certifications Section */}
        <section className="py-20 px-5 bg-white" data-testid="certifications-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={{ color: "#1F2937" }}>
              Certifications & Technology Partners
            </h2>
            <p className="text-lg text-center mb-16" style={{ color: "#4A5568" }}>
              We partner with industry-leading technology providers
            </p>
            <div className="max-w-2xl mx-auto rounded-lg p-8" style={{ backgroundColor: "#F9FAFB", borderLeft: "4px solid #3B82F6" }}>
              <h3 className="text-xl font-bold mb-6" style={{ color: "#1F2937" }}>
                Our team holds certifications in:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <CertItem text="Microsoft technologies" />
                <CertItem text="Network security" />
                <CertItem text="Cloud services" />
                <CertItem text="Cybersecurity" />
                <CertItem text="IT infrastructure" />
                <CertItem text="Project management" />
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 px-5" style={{ backgroundColor: "#F9FAFB" }} data-testid="team-section">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: "#1F2937" }}>
              Our Expert Team
            </h2>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-10 md:p-12 text-center">
              <Users size={80} style={{ color: "#3B82F6" }} className="mx-auto mb-6" />
              <p className="text-lg leading-relaxed mb-6" style={{ color: "#4A5568", lineHeight: "1.8" }}>
                Our team of IT professionals is dedicated to your success. With expertise spanning network infrastructure, cybersecurity, cloud services, and IT strategy, we have the knowledge and experience to handle all your technology needs.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.8" }}>
                We're committed to ongoing education and staying current with the latest technologies, ensuring you always receive the best possible service and advice.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-5" style={{ background: "linear-gradient(to right, #2C5F2D, #1a3d1b)" }} data-testid="cta-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Work Together?
            </h2>
            <p className="text-lg mb-10" style={{ color: "rgba(255,255,255,0.9)" }}>
              Let's discuss how we can support your business
            </p>
            <Button
              onClick={handleGetStarted}
              data-testid="cta-btn"
              className="text-lg font-bold px-10 py-6 rounded-lg hover:bg-gray-100 transition-all"
              style={{ backgroundColor: "white", color: "#2C5F2D" }}
            >
              Get Started
            </Button>
            <p className="mt-5 text-white">
              Call{" "}
              <a href="tel:7783497449" className="font-semibold hover:underline" data-testid="cta-phone">
                (778) 349-7449
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Helper Components
const ValueCard = ({ Icon, title, description, color }) => (
  <div className="bg-white rounded-xl p-10 text-center" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
    <Icon size={56} style={{ color }} className="mx-auto mb-5" />
    <h3 className="text-2xl font-bold mb-4" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>{description}</p>
  </div>
);

const BenefitItem = ({ text }) => (
  <div className="flex items-center gap-4 py-3">
    <Check size={24} style={{ color: "#2C5F2D", flexShrink: 0 }} />
    <span className="text-lg" style={{ color: "#374151" }}>{text}</span>
  </div>
);

const ApproachCard = ({ Icon, title, description, color }) => (
  <div className="bg-white rounded-lg p-10 text-center" style={{ borderTop: `4px solid ${color}` }}>
    <Icon size={48} style={{ color }} className="mx-auto mb-5" />
    <h3 className="text-xl font-bold mb-4" style={{ color: "#1F2937" }}>{title}</h3>
    <p className="text-base leading-relaxed" style={{ color: "#4A5568", lineHeight: "1.7" }}>{description}</p>
  </div>
);

const CertItem = ({ text }) => (
  <div className="flex items-start gap-3 py-2">
    <span style={{ color: "#3B82F6", marginTop: "6px" }}>•</span>
    <span className="text-base" style={{ color: "#374151" }}>{text}</span>
  </div>
);

export default AboutPage;
